import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Input, InputGroup, FormGroup, Card, Button } from "reactstrap";
import { Search, Grid, List, Star, X, ChevronDown, ChevronUp } from "react-feather";
import { IApplicationState } from "../../redux/reducers";
import { IIngredient } from "../../interfaces/ingredient";
import { IPagination } from "../../interfaces/pagination";
import { IUser } from "../../interfaces/user";
import ingredientService from "../../services/ingredientService";
import { PAGINATION_META_DEFAULT } from "../../utils/constants";
import ReactImageFallback from "react-image-fallback";
import { getIngredientImagePlaceholder } from "../../utils/ingredient";
import { ImageSkeleton } from "../../utils/ghostUI";
import config from "../../utils/config";
import Pagination from "../../components/pagination";
import { routes } from "../routes";
import { useDebounce } from "../../utils/hooks";
import { error as showError } from "../../utils/notification";
import "./supplierSearchIngredients.scss";

interface ISearchResponse {
	data: IIngredient[];
	meta: IPagination;
}

interface IProps extends RouteComponentProps {
	profile: IUser | undefined;
	searchIngredient: (name: string, type?: string, page?: number) => Promise<ISearchResponse>;
	starIngredient: (id: string, isStarred: boolean) => Promise<void>;
}

const COUNTRIES = ["USA", "Poland", "Canada", "Mexico", "Chile"];
const FORMS = ["Solid", "Powder", "Liquid"];

const SupplierSearchIngredients: React.FC<IProps> = (props) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [ingredients, setIngredients] = useState<IIngredient[]>([]);
	const [pagination, setPagination] = useState<IPagination>(PAGINATION_META_DEFAULT);
	const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
	const [sortBy, setSortBy] = useState<string>("Recommended");

	// Filter states
	const [selectedCountries, setSelectedCountries] = useState<string[]>(["USA"]);
	const [selectedForms, setSelectedForms] = useState<string[]>(["Solid"]);
	const [distance, setDistance] = useState<number>(0);
	const [minPrice, setMinPrice] = useState<number>(0);
	const [maxPrice, setMaxPrice] = useState<number>(1000);
	const [journeyAIOnly, setJourneyAIOnly] = useState<boolean>(false);

	// Collapsible filter states
	const [expandedFilters, setExpandedFilters] = useState<{ [key: string]: boolean }>({
		country: true,
		form: true,
		distance: true,
		priceRange: true,
		journeyAI: true
	});

	// Debounce search term to reduce API calls
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const performSearchRef = useRef<() => Promise<void>>();

	const performSearch = useCallback(async () => {
		if (!debouncedSearchTerm.trim()) {
			setIngredients([]);
			setPagination(PAGINATION_META_DEFAULT);
			return;
		}

		setLoading(true);
		try {
			const response = await props.searchIngredient(
				debouncedSearchTerm,
				"paginated",
				pagination.page
			);
			setIngredients(Array.isArray(response.data) ? response.data : []);
			setPagination(response.meta || PAGINATION_META_DEFAULT);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Failed to search ingredients";
			showError({
				title: "Search Error",
				description: errorMessage
			});
			setIngredients([]);
			setPagination(PAGINATION_META_DEFAULT);
		} finally {
			setLoading(false);
		}
	}, [debouncedSearchTerm, pagination.page, props]);

	// Store ref for useEffect
	performSearchRef.current = performSearch;

	useEffect(() => {
		performSearchRef.current?.();
	}, [debouncedSearchTerm, pagination.page]);

	// Initialize CSS variables for colored sliders
	useEffect(() => {
		// Calculate percentage for distance slider (accounting for thumb center)
		const distancePercent = (distance / 500) * 100;
		const minPercent = (minPrice / 1000) * 100;
		const maxPercent = (maxPrice / 1000) * 100;

		// Set CSS variables
		document.documentElement.style.setProperty("--distance-percent", `${distancePercent}%`);
		document.documentElement.style.setProperty("--min-percent", `${minPercent}%`);
		document.documentElement.style.setProperty("--max-percent", `${maxPercent}%`);
	}, [distance, minPrice, maxPrice]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setPagination((prev) => ({ ...prev, page: 1 }));
	};

	const handleCountryToggle = (country: string) => {
		setSelectedCountries((prev) =>
			prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
		);
		setPagination((prev) => ({ ...prev, page: 1 }));
	};

	const handleFormToggle = (form: string) => {
		setSelectedForms((prev) =>
			prev.includes(form) ? prev.filter((f) => f !== form) : [...prev, form]
		);
		setPagination((prev) => ({ ...prev, page: 1 }));
	};

	const clearAllFilters = () => {
		setSelectedCountries([]);
		setSelectedForms([]);
		setDistance(0);
		setMinPrice(0);
		setMaxPrice(1000);
		setJourneyAIOnly(false);
		setPagination((prev) => ({ ...prev, page: 1 }));
	};

	const toggleFilter = (filterName: string) => {
		setExpandedFilters((prev) => ({
			...prev,
			[filterName]: !prev[filterName]
		}));
	};

	const handlePageChange = (page: number) => {
		setPagination((prev) => ({ ...prev, page }));
	};

	const handleStarIngredient = async (id: string, isStarred: boolean) => {
		try {
			await props.starIngredient(id, isStarred);
			// Update local state to reflect star change
			setIngredients((prev) =>
				prev.map((ing) => (ing.id === id ? { ...ing, is_starred: !isStarred } : ing))
			);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Failed to update ingredient";
			showError({
				title: "Error",
				description: errorMessage
			});
		}
	};

	const handleIngredientClick = (id: string) => {
		props.history.push(`${routes.INGREDIENTS}/${id}`);
	};

	const activeFiltersCount =
		selectedCountries.length +
		selectedForms.length +
		(distance !== 0 ? 1 : 0) +
		(minPrice !== 0 || maxPrice !== 1000 ? 1 : 0) +
		(journeyAIOnly ? 1 : 0);

	return (
		<div className="supplier-search-ingredients">
			<div className="search-ingredients-container">
				{/* Left Sidebar - Filters */}
				<div className="filters-sidebar">
					<div className="filters-header">
						<h5>Filters</h5>
						{activeFiltersCount > 0 && (
							<Button color="link" className="clear-all-btn" onClick={clearAllFilters}>
								Clear all
							</Button>
						)}
					</div>

					{/* Country Filter */}
					<div className="filter-section">
						<div className="filter-header" onClick={() => toggleFilter("country")}>
							<span>Country</span>
							{expandedFilters.country ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</div>
						{expandedFilters.country && (
							<div className="filter-content">
								<Input type="text" placeholder="Search country..." className="filter-search" />
								<div className="filter-checkboxes">
									{COUNTRIES.map((country) => (
										<label key={country} className="filter-checkbox">
											<input
												type="checkbox"
												checked={selectedCountries.includes(country)}
												onChange={() => handleCountryToggle(country)}
											/>
											<span>{country}</span>
										</label>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Form Filter */}
					<div className="filter-section">
						<div className="filter-header" onClick={() => toggleFilter("form")}>
							<span>Form</span>
							{expandedFilters.form ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</div>
						{expandedFilters.form && (
							<div className="filter-content">
								<div className="filter-checkboxes">
									{FORMS.map((form) => (
										<label key={form} className="filter-checkbox">
											<input
												type="checkbox"
												checked={selectedForms.includes(form)}
												onChange={() => handleFormToggle(form)}
											/>
											<span>{form}</span>
										</label>
									))}
								</div>
							</div>
						)}
					</div>

					{/* Distance Filter */}
					<div className="filter-section">
						<div className="filter-header" onClick={() => toggleFilter("distance")}>
							<span>Distance</span>
							{expandedFilters.distance ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</div>
						{expandedFilters.distance && (
							<div className="filter-content">
								<Input
									type="text"
									value={`${distance} miles away`}
									readOnly
									className="distance-input"
								/>
								<div
									className="distance-slider-wrapper"
									style={
										{
											"--distance-percent": `${(distance / 500) * 100}%`
										} as React.CSSProperties
									}
								>
									<input
										type="range"
										min="0"
										max="500"
										value={distance}
										onChange={(e) => {
											const val = Number(e.target.value);
											setDistance(val);
											// Update CSS variable for colored track
											const percent = (val / 500) * 100;
											document.documentElement.style.setProperty(
												"--distance-percent",
												`${percent}%`
											);
										}}
										className="distance-slider"
									/>
								</div>
							</div>
						)}
					</div>

					{/* Price Range Filter */}
					<div className="filter-section">
						<div className="filter-header" onClick={() => toggleFilter("priceRange")}>
							<span>Price Range</span>
							{expandedFilters.priceRange ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</div>
						{expandedFilters.priceRange && (
							<div className="filter-content">
								<div className="price-inputs">
									<Input
										type="number"
										placeholder="Min. price"
										value={minPrice}
										onChange={(e) => {
											const val = Number(e.target.value);
											if (val <= maxPrice) setMinPrice(val);
										}}
										min="0"
										max={maxPrice}
									/>
									<Input
										type="number"
										placeholder="Max. price"
										value={maxPrice}
										onChange={(e) => {
											const val = Number(e.target.value);
											if (val >= minPrice) setMaxPrice(val);
										}}
										min={minPrice}
										max="1000"
									/>
								</div>
								<div
									className="dual-range-container"
									style={
										{
											"--min-percent": `${(minPrice / 1000) * 100}%`,
											"--max-percent": `${(maxPrice / 1000) * 100}%`
										} as React.CSSProperties
									}
								>
									<input
										type="range"
										min="0"
										max="1000"
										value={minPrice}
										onChange={(e) => {
											const val = Number(e.target.value);
											if (val <= maxPrice) {
												setMinPrice(val);
												document.documentElement.style.setProperty(
													"--min-percent",
													`${(val / 1000) * 100}%`
												);
											}
										}}
										className="price-slider price-slider-min"
									/>
									<input
										type="range"
										min="0"
										max="1000"
										value={maxPrice}
										onChange={(e) => {
											const val = Number(e.target.value);
											if (val >= minPrice) {
												setMaxPrice(val);
												document.documentElement.style.setProperty(
													"--max-percent",
													`${(val / 1000) * 100}%`
												);
											}
										}}
										className="price-slider price-slider-max"
									/>
								</div>
							</div>
						)}
					</div>

					{/* JourneyAI Filter */}
					<div className="filter-section">
						<div className="filter-header" onClick={() => toggleFilter("journeyAI")}>
							<span>JourneyAI results only</span>
							{expandedFilters.journeyAI ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						</div>
						{expandedFilters.journeyAI && (
							<div className="filter-content">
								<label className="toggle-switch">
									<input
										type="checkbox"
										checked={journeyAIOnly}
										onChange={(e) => setJourneyAIOnly(e.target.checked)}
									/>
									<span className="slider"></span>
								</label>
							</div>
						)}
					</div>
				</div>

				{/* Main Content Area */}
				<div className="main-content">
					{/* Header */}
					<div className="content-header">
						<div>
							<h4 className="page-title">Search Ingredients</h4>
							{debouncedSearchTerm && (
								<p className="results-count">
									Showing {pagination.total} results for '{debouncedSearchTerm}'
								</p>
							)}
						</div>
						<div className="header-controls">
							<FormGroup className="sort-select">
								<span className="sort-label">Sort by:</span>
								<Input type="select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
									<option>Recommended</option>
									<option>Price: Low to High</option>
									<option>Price: High to Low</option>
									<option>Distance: Nearest First</option>
									<option>Name: A-Z</option>
								</Input>
							</FormGroup>
							<div className="view-toggle">
								<Button
									color={viewMode === "grid" ? "primary" : "light"}
									size="sm"
									onClick={() => setViewMode("grid")}
								>
									<Grid size={18} />
								</Button>
								<Button
									color={viewMode === "list" ? "primary" : "light"}
									size="sm"
									onClick={() => setViewMode("list")}
								>
									<List size={18} />
								</Button>
							</div>
						</div>
					</div>

					{/* Search Bar */}
					<div className="search-bar-container">
						<div className="input-group">
							<Search size={20} className="search-icon" />
							<Input
								type="text"
								placeholder="Search Ingredients"
								value={searchTerm}
								onChange={handleSearchChange}
								className="search-input"
							/>
							{searchTerm && (
								<X
									size={18}
									className="clear-icon"
									onClick={() => {
										setSearchTerm("");
										setIngredients([]);
									}}
								/>
							)}
						</div>
					</div>

					{/* Results Grid/List */}
					{loading ? (
						<div className="loading-state">Loading...</div>
					) : ingredients.length === 0 ? (
						<div className="empty-state">
							<p>No ingredients found. Try a different search term.</p>
						</div>
					) : (
						<>
							<div className={`ingredients-${viewMode}`}>
								{ingredients.map((ingredient) => (
									<Card
										key={ingredient.id}
										className="ingredient-card"
										onClick={() => handleIngredientClick(ingredient.id || "")}
									>
										<div className="card-header">
											<Star
												size={18}
												color={ingredient.is_starred ? "#f4a850" : "#657280"}
												fill={ingredient.is_starred ? "#f4a850" : "none"}
												onClick={(e) => {
													e.stopPropagation();
													if (ingredient.id) {
														handleStarIngredient(ingredient.id, ingredient.is_starred || false);
													}
												}}
											/>
										</div>
										<div className="card-image">
											<ReactImageFallback
												src={config.api.endpoints.ingredients.ingredientImage(
													ingredient.name || ""
												)}
												fallbackImage={getIngredientImagePlaceholder()}
												initialImage={<ImageSkeleton />}
											/>
										</div>
										<div className="card-body">
											<h6 className="ingredient-name">{ingredient.name || "Unknown Ingredient"}</h6>
											<p className="ingredient-supplier">
												{ingredient.manufacturer?.name || "Berry Farms A"}
											</p>
											<div className="ingredient-tags">
												{ingredient.is_organic && (
													<span className="tag organic">
														<span className="tag-icon">🌿</span>
														Organic
													</span>
												)}
												{ingredient.is_recommended && (
													<span className="tag ai">
														<span className="tag-icon">✨</span>
														AI
													</span>
												)}
											</div>
											<div className="card-price">${ingredient.price || 10}</div>
											<div className="card-actions">
												<Button
													color="outline-primary"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														// Handle "Try In" action
													}}
												>
													Try In
												</Button>
												<Button
													color="primary"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														// Handle "Add" action
													}}
												>
													+ Add
												</Button>
											</div>
										</div>
									</Card>
								))}
							</div>

							{/* Pagination */}
							<div className="pagination-container">
								<div className="pagination-info">
									<FormGroup className="items-per-page">
										<Input type="select" value={pagination.size}>
											<option>6</option>
											<option>12</option>
											<option>24</option>
											<option>48</option>
										</Input>
										<span>Showing items per page</span>
									</FormGroup>
									<span className="pagination-text">
										{pagination.page * pagination.size - pagination.size + 1}-
										{Math.min(pagination.page * pagination.size, pagination.total)} of{" "}
										{pagination.total} items
									</span>
								</div>
								<Pagination
									page={pagination.page}
									size={pagination.size}
									pages={pagination.pages}
									total={pagination.total}
									handlePageChange={handlePageChange}
								/>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	profile: state.user.profile
});

const mapDispatchToProps = {
	searchIngredient: (name: string, type?: string, page?: number) =>
		ingredientService.searchIngredient(name, type, page),
	starIngredient: (id: string, isStarred: boolean) =>
		ingredientService.starIngredient(id, isStarred)
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SupplierSearchIngredients));
